import { useState } from 'react';
import { Popover, Slider, Button, Space, Typography } from 'antd';
import styled from 'styled-components';

import {
  MIN_COLUMN_WIDTH,
  MAX_COLUMN_WIDTH,
  DEFAULT_COLUMN_WIDTH,
} from '../utils/constants';

const { Text } = Typography;

export interface IProps {
  open: boolean;
  title: string;
  currentWidth: number;
  children: React.ReactNode;
  onSave: (width: number) => void;
  onCancel: () => void;
  onLiveWidthChange?: (width: number) => void;
}

const ColumnResizePopover: React.FC<IProps> = ({
  open,
  title,
  currentWidth,
  children,
  onSave,
  onCancel,
  onLiveWidthChange,
}) => {
  const [pendingWidth, setPendingWidth] = useState<number | null>(null);

  const displayWidth = pendingWidth ?? currentWidth ?? DEFAULT_COLUMN_WIDTH;

  const handleSliderChange = (value: number) => {
    setPendingWidth(value);
    onLiveWidthChange?.(value);
  };

  const handleSave = () => {
    onSave(displayWidth);
    setPendingWidth(null);
  };

  const handleCancel = () => {
    setPendingWidth(null);
    onCancel();
  };

  const content = (
    <PopoverContent>
      <SliderWrapper>
        <Slider
          min={MIN_COLUMN_WIDTH}
          max={MAX_COLUMN_WIDTH}
          value={displayWidth}
          onChange={handleSliderChange}
          tooltip={{ formatter: val => `${val}px` }}
        />
        <WidthDisplay>
          <Text type="secondary">{displayWidth}px</Text>
        </WidthDisplay>
      </SliderWrapper>
      <ButtonRow>
        <Button size="small" onClick={handleCancel}>
          Cancel
        </Button>
        <Button size="small" type="primary" onClick={handleSave}>
          Save
        </Button>
      </ButtonRow>
    </PopoverContent>
  );

  return (
    <Popover
      open={open}
      content={content}
      title={`Column width – ${title}`}
      trigger="click"
      placement="bottom"
      onOpenChange={visible => {
        if (!visible) {
          handleCancel();
        }
      }}
    >
      {children}
    </Popover>
  );
};

const PopoverContent = styled.div`
  width: 220px;
`;

const SliderWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.xs} 0;
`;

const WidthDisplay = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const ButtonRow = styled(Space)`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

export default ColumnResizePopover;
